import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Paper, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Autocomplete,
  Tooltip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Slider,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Save, 
  Plus, 
  Trash2, 
  Settings, 
  Search, 
  Layout, 
  Grid3X3,
  MousePointer2,
  CheckCircle2,
  ChevronDown,
  Minus,
  DoorClosed,
  RectangleHorizontal,
  Copy,
  Pencil,
  Activity,
  Type
} from 'lucide-react';
import GridLayout, { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import axios from 'axios';
import Icon from '@mdi/react';
import { 
  GlassContainer, 
  GlassmorphicCard, 
  PrimaryButton, 
  SecondaryButton,
  SectionTitle,
  BodyText,
  DangerButton
} from '../../styles/sharedStyles';
import IconPicker, { LucideIcons, MdiIcons, PRESET_COLORS } from './IconPicker';

const ReactGridLayout = GridLayout;

const RoomDesigner = ({ apiBaseUrl, token }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [designerTab, setDesignerTab] = useState(0); // 0: Designer, 1: Tools/Settings

  const [rooms, setRooms] = useState([]);
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false); // Track if we're creating a new room
  const [roomName, setRoomName] = useState('New Room');
  const [gridSize, setGridSize] = useState({ w: 30, h: 30 });
  const [floorType, setFloorType] = useState('default');
  const [items, setItems] = useState([]);
  const [haEntities, setHaEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Dialog state
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchRooms = useCallback(async (selectId = null) => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiBaseUrl}/api/floorplan/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRooms(res.data);
      if (selectId) {
        const room = res.data.find(r => r.id === selectId);
        if (room) loadRoom(room);
      } else if (res.data.length > 0 && !activeRoomId && !isCreatingNew) {
        // Only auto-load first room if we're not creating a new room
        setActiveRoomId(res.data[0].id);
        loadRoom(res.data[0]);
      }
    } catch (err) {
      setError('Failed to fetch rooms');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token, activeRoomId, isCreatingNew]);

  const fetchHAEntities = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBaseUrl}/api/homeassistant/entities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHaEntities(res.data);
    } catch (err) {
      console.error('Failed to fetch HA entities', err);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    fetchRooms();
    fetchHAEntities();
  }, [fetchRooms, fetchHAEntities]);

  const loadRoom = (room) => {
    setIsCreatingNew(false);
    setActiveRoomId(room.id);
    setRoomName(room.name);
    setGridSize({ w: room.grid_width, h: room.grid_height });
    setFloorType(room.floor_type || 'default');
    setItems(room.items.map(item => ({
      i: item.id.toString(),
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
      entity_id: item.entity_id || '',
      icon_name: item.icon_name,
      icon_type: item.icon_type || 'lucide',
      object_type: item.object_type || 'device',
      color_hex: item.color_hex,
      friendly_name: item.friendly_name || '',
      flow_speed: item.flow_speed || 1.0,
      flow_direction: item.flow_direction || 'normal',
      trigger_sensor: item.trigger_sensor || '',
      glow_enabled: item.glow_enabled !== undefined ? item.glow_enabled : true
    })));
  };

  const handleCreateNew = () => {
    setIsCreatingNew(true);
    setActiveRoomId(null);
    setRoomName('New Room');
    setGridSize({ w: 30, h: 30 });
    setFloorType('default');
    setItems([]);
  };

  const handleDeleteRoom = async () => {
    if (!activeRoomId) return;
    if (!window.confirm(`Delete room "${roomName}"?`)) return;

    try {
      await axios.delete(`${apiBaseUrl}/api/floorplan/rooms/${activeRoomId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Don't call fetchRooms here - manually reset state to avoid re-fetching
      setRooms(prev => prev.filter(r => r.id !== activeRoomId));
      setIsCreatingNew(true);
      setActiveRoomId(null);
      setRoomName('New Room');
      setGridSize({ w: 30, h: 30 });
      setItems([]);
    } catch (err) {
      setError('Failed to delete room');
    }
  };

  const handleSaveRoom = async () => {
    try {
      setSaving(true);
      const roomData = {
        name: roomName,
        grid_width: gridSize.w,
        grid_height: gridSize.h,
        floor_type: floorType,
        items: items.map(item => ({
          entity_id: item.entity_id || '',
          icon_name: item.icon_name || 'HelpCircle',
          icon_type: item.icon_type || 'lucide',
          object_type: item.object_type || 'device',
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
          color_hex: item.color_hex || '#00d4ff',
          friendly_name: item.friendly_name || '',
          flow_speed: item.flow_speed || 1.0,
          flow_direction: item.flow_direction || 'normal',
          trigger_sensor: item.trigger_sensor || '',
          glow_enabled: item.glow_enabled !== undefined ? item.glow_enabled : true
        }))
      };

      const res = await axios.post(`${apiBaseUrl}/api/floorplan/rooms`, roomData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsCreatingNew(false);
      fetchRooms(res.data.id);
    } catch (err) {
      setError('Failed to save room');
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = (iconData = { type: 'lucide', name: 'Lightbulb' }, structuralConfig = null) => {
    let w = 2, h = 2, object_type = 'device', entity_id = '';
    if (structuralConfig) {
      object_type = structuralConfig.object_type;
      w = structuralConfig.w;
      h = structuralConfig.h;
      entity_id = '';
    }
    const newItem = {
      i: `new-${Date.now()}`,
      x: 0,
      y: 0,
      w,
      h,
      icon_name: iconData.name,
      icon_type: iconData.type,
      entity_id,
      object_type,
      color_hex: structuralConfig?.color_hex || '#00d4ff',
      friendly_name: '',
      flow_speed: 1.0,
      flow_direction: 'normal',
      trigger_sensor: '',
      glow_enabled: true
    };
    setItems([...items, newItem]);
  };

  const renderIcon = (item, size, color) => {
    if (item.icon_type === 'custom') {
      return (
        <img 
          src={`/${item.icon_name}.png`} 
          alt={item.icon_name}
          style={{ 
            width: size, 
            height: size, 
            objectFit: 'contain'
          }}
        />
      );
    }
    if (item.icon_type === 'mdi' && MdiIcons[item.icon_name]) {
      return <Icon path={MdiIcons[item.icon_name]} size={size / 24} color={color} />;
    }
    const LucideIcon = LucideIcons[item.icon_name] || LucideIcons.HelpCircle;
    return <LucideIcon size={size} color={color} />;
  };

  const handleDeleteItem = (id) => {
    setItems(items.filter(item => item.i !== id));
  };

  const handleCopyItem = (item) => {
    const newItem = {
      ...item,
      i: `new-${Date.now()}`,
      x: item.x + 1,
      y: item.y + 1
    };
    setItems([...items, newItem]);
  };

  const onLayoutChange = (newLayout) => {
    setItems(prevItems => {
      return prevItems.map(item => {
        const layoutItem = newLayout.find(l => l.i === item.i);
        if (layoutItem) {
          return { ...item, x: layoutItem.x, y: layoutItem.y, w: layoutItem.w, h: layoutItem.h };
        }
        return item;
      });
    });
  };

  const isPipe = (type) => type?.startsWith('energy_pipe_');

  const handleItemClick = (item) => {
    setEditingItem({ ...item });
    setConfigDialogOpen(true);
  };

  const saveItemConfig = () => {
    setItems(items.map(item => item.i === editingItem.i ? editingItem : item));
    setConfigDialogOpen(false);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: isMobile ? 'column' : 'row',
      height: isMobile ? 'auto' : 'calc(100vh - 250px)', 
      gap: 2,
      minHeight: isMobile ? '600px' : 'auto'
    }}>
      {isMobile && (
        <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', mb: 1 }}>
          <Tabs
            value={designerTab}
            onChange={(e, val) => setDesignerTab(val)}
            variant="fullWidth"
            sx={{
              '& .MuiTabs-indicator': {
                background: 'linear-gradient(90deg, #00d4ff, #ff4081)',
                height: '2px'
              },
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.6)',
                textTransform: 'none',
                fontSize: '0.9rem',
                minHeight: '48px',
                '&.Mui-selected': { color: '#00d4ff' }
              }
            }}
          >
            <Tab icon={<Layout size={18} />} iconPosition="start" label="Canvas" />
            <Tab icon={<Settings size={18} />} iconPosition="start" label="Tools" />
          </Tabs>
        </Box>
      )}

       {/* Sidebar - Tools/Settings */}
      {(!isMobile || designerTab === 1) && (
        <GlassContainer sx={{ 
          width: isMobile ? '100%' : 300, 
          p: 2, 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 2,
          height: isMobile ? 'calc(100vh - 220px)' : '100%',
          minHeight: 0,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 212, 255, 0.2)',
            borderRadius: '10px',
            '&:hover': {
              background: 'rgba(0, 212, 255, 0.3)',
            },
          },
        }}>
          <SectionTitle sx={{ fontSize: isMobile ? '1rem' : '1.1rem' }}>
            <Settings size={isMobile ? 18 : 20} /> Room Settings
          </SectionTitle>
          
          <Autocomplete
            options={[{ id: 'new', name: '+ Create New Room', isCreate: true }, ...rooms]}
            getOptionLabel={(option) => option.name || ''}
            value={isCreatingNew ? { id: 'new', name: '+ Create New Room', isCreate: true } : (rooms.find(r => r.id === activeRoomId) || null)}
            isOptionEqualToValue={(option, value) => {
              if (value?.isCreate) return option.isCreate;
              return option.id === value?.id;
            }}
            onChange={(e, val) => {
              if (val?.isCreate) {
                setIsCreatingNew(true);
                setActiveRoomId(null);
                setRoomName('New Room');
                setGridSize({ w: 30, h: 30 });
                setItems([]);
              } else if (val) {
                setIsCreatingNew(false);
                loadRoom(val);
              } else {
                // Cleared - stay in create new mode
                setIsCreatingNew(true);
                setActiveRoomId(null);
                setRoomName('New Room');
                setGridSize({ w: 30, h: 30 });
                setItems([]);
              }
              if (isMobile) setDesignerTab(0); // Switch to canvas after selection
            }}
            renderInput={(params) => (
              <TextField {...params} label="Select Room" fullWidth size="small" />
            )}
            sx={{ 
              '& .MuiOutlinedInput-root': { color: 'white' },
              '& .MuiAutocomplete-paper': { bgcolor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)' }
            }}
            slotProps={{
              paper: {
                sx: {
                  bgcolor: '#1a1a1a',
                  backgroundImage: 'none',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.1)',
                  '& .MuiAutocomplete-listbox': {
                    '& .MuiAutocomplete-option': {
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                      '&.Mui-focused': { bgcolor: 'rgba(255,255,255,0.15)' }
                    }
                  }
                }
              }
            }}
          />

          <TextField
            label="Room Name"
            fullWidth
            size="small"
            value={roomName}
            onChange={(e) => setRoomName(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { color: 'white' } }}
          />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Cols"
              type="number"
              size="small"
              value={gridSize.w}
              onChange={(e) => setGridSize({ ...gridSize, w: parseInt(e.target.value) || 30 })}
            />
            <TextField
              label="Rows"
              type="number"
              size="small"
              value={gridSize.h}
              onChange={(e) => setGridSize({ ...gridSize, h: parseInt(e.target.value) || 30 })}
            />
          </Box>

          <Autocomplete
            options={[
              { id: 'default', name: 'Dark Grid' },
              { id: 'concrete', name: 'Concrete' },
              { id: 'tiles', name: 'Modern Tiles' },
              { id: 'wood', name: 'Light Wood' },
              { id: 'carpet', name: 'Carpet' },
              { id: 'stone', name: 'Stone' },
              { id: 'vinyl', name: 'Vinyl Plank' }
            ]}
            getOptionLabel={(option) => option.name}
            value={[
              { id: 'default', name: 'Dark Grid' },
              { id: 'concrete', name: 'Concrete' },
              { id: 'tiles', name: 'Modern Tiles' },
              { id: 'wood', name: 'Light Wood' },
              { id: 'carpet', name: 'Carpet' },
              { id: 'stone', name: 'Stone' },
              { id: 'vinyl', name: 'Vinyl Plank' }
            ].find(o => o.id === floorType) || null}
            onChange={(e, val) => setFloorType(val?.id || 'default')}
            renderInput={(params) => (
              <TextField {...params} label="Room Floor" fullWidth size="small" />
            )}
            sx={{ 
              '& .MuiOutlinedInput-root': { color: 'white' }
            }}
            slotProps={{
              paper: {
                sx: {
                  bgcolor: '#1a1a1a',
                  backgroundImage: 'none',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.1)',
                }
              }
            }}
          />

          <SectionTitle sx={{ fontSize: isMobile ? '1rem' : '1.1rem', mt: 1 }}>
            <Layout size={isMobile ? 18 : 20} /> Components
          </SectionTitle>
          <Box sx={{ overflowY: 'auto', flexGrow: 1, pr: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>Devices</Typography>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {['Lightbulb', 'Power', 'Thermometer', 'Activity', 'Shield', 'Tv', 'Speaker', 'Fan', 'DoorClosed', 'Wind'].map(icon => (
                <Grid item xs={isMobile ? 3 : 4} key={icon}>
                  <IconButton 
                    onClick={() => {
                      handleAddItem({ type: 'lucide', name: icon });
                      if (isMobile) setDesignerTab(0);
                    }}
                    sx={{ 
                      width: '100%', 
                      aspectRatio: '1/1',
                      borderRadius: 2,
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.7)',
                      '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.1)', color: '#00d4ff' }
                    }}
                  >
                    {LucideIcons[icon] ? React.createElement(LucideIcons[icon], { size: isMobile ? 20 : 24 }) : icon}
                  </IconButton>
                </Grid>
              ))}
              <Grid item xs={isMobile ? 3 : 4}>
                <IconButton 
                  onClick={() => { setEditingItem(null); setIconPickerOpen(true); }}
                  sx={{ 
                    width: '100%', 
                    aspectRatio: '1/1',
                    borderRadius: 2,
                    border: '1px dashed rgba(255,255,255,0.3)',
                    color: 'rgba(255,255,255,0.7)'
                  }}
                >
                  <Plus size={isMobile ? 20 : 24} />
                </IconButton>
              </Grid>
            </Grid>

            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>Text Labels</Typography>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <IconButton 
                  onClick={() => {
                    handleAddItem({ type: 'lucide', name: 'Type' }, { object_type: 'text_label', w: 4, h: 1, color_hex: '#ffffff' });
                    if (isMobile) setDesignerTab(0);
                  }}
                  sx={{ 
                    width: '100%', 
                    py: 1,
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.7)',
                    '&:hover': { bgcolor: 'rgba(0, 212, 255,0.1)', color: '#00d4ff' }
                  }}
                >
                  <Typography variant="body2" sx={{ mr: 1 }}>Text Label</Typography>
                  <Type size={20} />
                </IconButton>
              </Grid>
            </Grid>

            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>Walls</Typography>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Tooltip title="Wall (H)">
                  <IconButton 
                    onClick={() => {
                      handleAddItem({ type: 'lucide', name: 'Minus' }, { object_type: 'wall_h', w: 6, h: 1, color_hex: '#666666' });
                      if (isMobile) setDesignerTab(0);
                    }}
                    sx={{ width: '100%', py: 1, borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.1)', color: '#00d4ff' } }}
                  >
                    <Minus size={20} />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item xs={6}>
                <Tooltip title="Wall (V)">
                  <IconButton 
                    onClick={() => {
                      handleAddItem({ type: 'lucide', name: 'Minus' }, { object_type: 'wall_v', w: 1, h: 6, color_hex: '#666666' });
                      if (isMobile) setDesignerTab(0);
                    }}
                    sx={{ width: '100%', py: 1, borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.1)', color: '#00d4ff' } }}
                  >
                    <Minus size={20} style={{ transform: 'rotate(90deg)' }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>

            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>Energy Pipes</Typography>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Tooltip title="Green Pipe (H)">
                  <IconButton 
                    onClick={() => {
                      handleAddItem({ type: 'lucide', name: 'Activity' }, { object_type: 'energy_pipe_h_green', w: 6, h: 1, color_hex: '#00ff88' });
                      if (isMobile) setDesignerTab(0);
                    }}
                    sx={{ width: '100%', py: 1, borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(0, 255, 136, 0.7)', '&:hover': { bgcolor: 'rgba(0, 255, 136, 0.1)', color: '#00ff88' } }}
                  >
                    <Activity size={20} />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item xs={6}>
                <Tooltip title="Green Pipe (V)">
                  <IconButton 
                    onClick={() => {
                      handleAddItem({ type: 'lucide', name: 'Activity' }, { object_type: 'energy_pipe_v_green', w: 1, h: 6, color_hex: '#00ff88' });
                      if (isMobile) setDesignerTab(0);
                    }}
                    sx={{ width: '100%', py: 1, borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(0, 255, 136, 0.7)', '&:hover': { bgcolor: 'rgba(0, 255, 136, 0.1)', color: '#00ff88' } }}
                  >
                    <Activity size={20} style={{ transform: 'rotate(90deg)' }} />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item xs={6}>
                <Tooltip title="Red Pipe (H)">
                  <IconButton 
                    onClick={() => {
                      handleAddItem({ type: 'lucide', name: 'Activity' }, { object_type: 'energy_pipe_h_red', w: 6, h: 1, color_hex: '#ff4444' });
                      if (isMobile) setDesignerTab(0);
                    }}
                    sx={{ width: '100%', py: 1, borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255, 68, 68, 0.7)', '&:hover': { bgcolor: 'rgba(255, 68, 68, 0.1)', color: '#ff4444' } }}
                  >
                    <Activity size={20} />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item xs={6}>
                <Tooltip title="Red Pipe (V)">
                  <IconButton 
                    onClick={() => {
                      handleAddItem({ type: 'lucide', name: 'Activity' }, { object_type: 'energy_pipe_v_red', w: 1, h: 6, color_hex: '#ff4444' });
                      if (isMobile) setDesignerTab(0);
                    }}
                    sx={{ width: '100%', py: 1, borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255, 68, 68, 0.7)', '&:hover': { bgcolor: 'rgba(255, 68, 68, 0.1)', color: '#ff4444' } }}
                  >
                    <Activity size={20} style={{ transform: 'rotate(90deg)' }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>

            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>Doors & Windows</Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Tooltip title="Door (H)">
                  <IconButton 
                    onClick={() => {
                      handleAddItem({ type: 'lucide', name: 'DoorClosed' }, { object_type: 'door_h', w: 3, h: 1, color_hex: '#8B4513' });
                      if (isMobile) setDesignerTab(0);
                    }}
                    sx={{ width: '100%', py: 1, borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.1)', color: '#00d4ff' } }}
                  >
                    <DoorClosed size={20} />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item xs={6}>
                <Tooltip title="Door (V)">
                  <IconButton 
                    onClick={() => {
                      handleAddItem({ type: 'lucide', name: 'DoorClosed' }, { object_type: 'door_v', w: 1, h: 3, color_hex: '#8B4513' });
                      if (isMobile) setDesignerTab(0);
                    }}
                    sx={{ width: '100%', py: 1, borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.1)', color: '#00d4ff' } }}
                  >
                    <DoorClosed size={20} style={{ transform: 'rotate(90deg)' }} />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item xs={6}>
                <Tooltip title="Window (H)">
                  <IconButton 
                    onClick={() => {
                      handleAddItem({ type: 'lucide', name: 'RectangleHorizontal' }, { object_type: 'window_h', w: 4, h: 1, color_hex: '#87CEEB' });
                      if (isMobile) setDesignerTab(0);
                    }}
                    sx={{ width: '100%', py: 1, borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.1)', color: '#00d4ff' } }}
                  >
                    <RectangleHorizontal size={20} />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item xs={6}>
                <Tooltip title="Window (V)">
                  <IconButton 
                    onClick={() => {
                      handleAddItem({ type: 'lucide', name: 'RectangleHorizontal' }, { object_type: 'window_v', w: 1, h: 4, color_hex: '#87CEEB' });
                      if (isMobile) setDesignerTab(0);
                    }}
                    sx={{ width: '100%', py: 1, borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.1)', color: '#00d4ff' } }}
                  >
                    <RectangleHorizontal size={20} style={{ transform: 'rotate(90deg)' }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <Button 
              startIcon={saving ? <CircularProgress size={12} color="inherit" /> : <Save size={12} />}
              onClick={handleSaveRoom}
              disabled={saving}
              variant="outlined"
              sx={{ 
                py: 0.3,
                fontSize: '0.75rem',
                bgcolor: '#1a4d2e',
                color: '#fff',
                border: '1px solid rgba(0, 255, 136, 0.3)',
                '&:hover': { 
                  bgcolor: '#2d7a4e',
                  border: '1px solid rgba(0, 255, 136, 0.5)',
                  color: '#fff',
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(26, 77, 46, 0.5)',
                  border: '1px solid rgba(0, 255, 136, 0.2)',
                  color: 'rgba(255, 255, 255, 0.5)',
                },
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: 'none',
                minHeight: 'auto'
              }}
            >
              Save Layout
            </Button>
            
            {activeRoomId && (
              <DangerButton 
                startIcon={<Trash2 size={12} />}
                onClick={handleDeleteRoom}
                sx={{ 
                  py: 0.3, 
                  fontSize: '0.75rem',
                  borderRadius: '8px',
                  minHeight: 'auto'
                }}
              >
                Delete Room
              </DangerButton>
            )}
          </Box>
        </GlassContainer>
      )}

      {/* Main Designer Area */}
      {(!isMobile || designerTab === 0) && (
        <GlassContainer sx={{ 
          flexGrow: 1, 
          p: 0, 
          position: 'relative', 
          overflow: 'auto',
          height: '100%',
          minHeight: 0,
          bgcolor: 'rgba(0,0,0,0.3)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: isMobile ? '12px' : '24px',
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.02)',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 212, 255, 0.2)',
            borderRadius: '10px',
          },
        }}>
          <Box sx={{ 
            width: gridSize.w * 20, 
            height: gridSize.h * 20,
            ...(floorType === 'concrete' ? {
              backgroundColor: '#333',
              backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
              backgroundSize: '20px 20px',
            } : floorType === 'tiles' ? {
              backgroundColor: '#1a1a1a',
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
              backgroundSize: '50px 50px',
            } : floorType === 'wood' ? {
              backgroundColor: '#3d2b1f',
              backgroundImage: 'linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)',
              backgroundSize: '20px 100%',
            } : floorType === 'carpet' ? {
              backgroundColor: '#5a4a42',
              backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px)',
              backgroundSize: '15px 15px',
            } : floorType === 'stone' ? {
              backgroundColor: '#4a5568',
              backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.03) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.03) 75%, rgba(255,255,255,0.03)), linear-gradient(45deg, rgba(255,255,255,0.03) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.03) 75%, rgba(255,255,255,0.03))',
              backgroundSize: '30px 30px',
              backgroundPosition: '0 0, 15px 15px',
            } : floorType === 'vinyl' ? {
              backgroundColor: '#8b7355',
              backgroundImage: 'linear-gradient(90deg, rgba(0,0,0,0.08) 1px, transparent 1px)',
              backgroundSize: '30px 100%',
            } : {
              backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
              backgroundSize: '20px 20px',
            }),
            position: 'relative',
          }}>
            <ReactGridLayout
              className="layout"
              layout={items}
              cols={gridSize.w}
              width={gridSize.w * 20}
              rowHeight={20}
              onLayoutChange={onLayoutChange}
              margin={[0, 0]}
              draggableHandle=".drag-handle"
              compactType={null}
              verticalCompact={false}
              preventCollision={true}
            >
              {items.map(item => (
                <div key={item.i} style={{ zIndex: isPipe(item.object_type) ? 9999 : (item.object_type?.startsWith('wall_') ? 5 : 10), position: 'relative' }}>
                  <Box 
                    sx={{ 
                      width: '100%',
                      height: '100%',
                      bgcolor: item.object_type?.startsWith('wall_') || isPipe(item.object_type) ? 'transparent' : 'rgba(255, 255, 255, 0.05)', 
                      border: item.object_type?.startsWith('wall_') || isPipe(item.object_type) ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: item.object_type?.startsWith('door') || item.object_type?.startsWith('window') ? 2 : 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      backdropFilter: item.object_type?.startsWith('wall_') || isPipe(item.object_type) ? 'none' : 'blur(8px)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: item.object_type?.startsWith('wall_') || isPipe(item.object_type) ? 'transparent' : 'rgba(255, 255, 255, 0.1)',
                      },
                      '&:hover .action-btns': { opacity: 1 },
                      '&:active .action-btns': { opacity: 1 },
                    }}
                  >
                    <Box className="drag-handle" sx={{ 
                      position: 'absolute', 
                      top: 0, left: 0, right: 0, bottom: 0, 
                      cursor: 'move',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {item.object_type === 'text_label' ? (
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: item.color_hex || '#ffffff',
                            fontWeight: 600,
                            textAlign: 'center',
                            fontSize: '0.8rem',
                            lineHeight: 1.1,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                          }}
                        >
                          {item.friendly_name || 'Text Label'}
                        </Typography>
                      ) : ['wall_h', 'wall_v', 'door_h', 'door_v', 'window_h', 'window_v', 'energy_pipe_h_green', 'energy_pipe_v_green', 'energy_pipe_h_red', 'energy_pipe_v_red'].includes(item.object_type) ? (
                        <Box sx={{
                          ...(item.object_type.startsWith('wall_') || isPipe(item.object_type)) ? {
                            bgcolor: item.color_hex || '#666',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3), inset 0 1px 1px rgba(255,255,255,0.1)',
                          } : {
                            bgcolor: `${item.color_hex || '#8B4513'}40`,
                            border: `2px solid ${item.color_hex || '#8B4513'}`,
                          },
                          borderRadius: '8px',
                          ...(item.object_type === 'wall_h' && { 
                            height: '6px', 
                            width: '100%', 
                            borderRadius: '3px',
                            background: `linear-gradient(to bottom, ${item.color_hex || '#666'}, ${(item.color_hex || '#666')}dd)`
                          }),
                        ...(item.object_type === 'wall_v' && { 
                            width: '6px', 
                            height: '100%', 
                            borderRadius: '3px',
                            background: `linear-gradient(to right, ${item.color_hex || '#666'}, ${(item.color_hex || '#666')}dd)`
                          }),
                        ...(item.object_type === 'energy_pipe_h_green' && { height: '12px', width: '100%', borderRadius: '10px', boxShadow: '0 0 15px rgba(0,255,136,0.4)', bgcolor: '#00ff88', border: '1px solid rgba(255,255,255,0.3)' }),
                        ...(item.object_type === 'energy_pipe_v_green' && { width: '12px', height: '100%', borderRadius: '10px', boxShadow: '0 0 15px rgba(0,255,136,0.4)', bgcolor: '#00ff88', border: '1px solid rgba(255,255,255,0.3)' }),
                        ...(item.object_type === 'energy_pipe_h_red' && { height: '12px', width: '100%', borderRadius: '10px', boxShadow: '0 0 15px rgba(255,68,68,0.4)', bgcolor: '#ff4444', border: '1px solid rgba(255,255,255,0.3)' }),
                        ...(item.object_type === 'energy_pipe_v_red' && { width: '12px', height: '100%', borderRadius: '10px', boxShadow: '0 0 15px rgba(255,68,68,0.4)', bgcolor: '#ff4444', border: '1px solid rgba(255,255,255,0.3)' }),
                        ...(item.object_type === 'door_h' && { 
                          height: '100%', width: '100%', bgcolor: 'transparent', border: 'none',
                          '&::before': {
                            content: '""', position: 'absolute', left: 0, bottom: 0, width: '100%', height: '4px',
                            bgcolor: item.color_hex || '#8B4513', borderRadius: '2px', transform: 'rotate(-25deg)', transformOrigin: 'left center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                          },
                          '&::after': {
                            content: '""', position: 'absolute', left: 0, bottom: 0, width: '100%', height: '100%',
                            border: `1px dashed ${item.color_hex || '#8B4513'}80`, borderRadius: '0 100% 0 0', borderLeft: 'none', borderBottom: 'none'
                          }
                        }),
                          ...(item.object_type === 'door_v' && { 
                            width: '100%', height: '100%', bgcolor: 'transparent', border: 'none',
                            '&::before': {
                              content: '""', position: 'absolute', top: 0, right: 0, height: '100%', width: '4px',
                              bgcolor: item.color_hex || '#8B4513', borderRadius: '2px', transform: 'rotate(-25deg)', transformOrigin: 'center top',
                              boxShadow: '2px 0 4px rgba(0,0,0,0.3)'
                            },
                            '&::after': {
                              content: '""', position: 'absolute', top: 0, right: 0, width: '100%', height: '100%',
                              border: `1px dashed ${item.color_hex || '#8B4513'}80`, borderRadius: '0 0 0 100%', borderRight: 'none', borderTop: 'none'
                            }
                          }),
                          ...(item.object_type === 'window_h' && { 
                            height: '8px', width: '100%', 
                            background: `linear-gradient(to bottom, ${item.color_hex || '#87CEEB'}80, ${item.color_hex || '#87CEEB'}20)`,
                            border: `1.5px solid ${item.color_hex || '#87CEEB'}`,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2), inset 0 0 10px rgba(255,255,255,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            '&::before': {
                              content: '""', width: '40%', height: '1px', bgcolor: 'rgba(255,255,255,0.5)', transform: 'rotate(-45deg)'
                            }
                          }),
                          ...(item.object_type === 'window_v' && { 
                            width: '8px', height: '100%', 
                            background: `linear-gradient(to right, ${item.color_hex || '#87CEEB'}80, ${item.color_hex || '#87CEEB'}20)`,
                            border: `1.5px solid ${item.color_hex || '#87CEEB'}`,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2), inset 0 0 10px rgba(255,255,255,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            '&::before': {
                              content: '""', height: '40%', width: '1px', bgcolor: 'rgba(255,255,255,0.5)', transform: 'rotate(-45deg)'
                            }
                          }),
                        }} />
                      ) : renderIcon(item, Math.min(item.w, item.h) * 8.4, item.color_hex || '#00d4ff')}
                    </Box>
                    <Box className="action-btns" sx={{ 
                      position: 'absolute', 
                      top: -12, right: -12, 
                      display: 'flex',
                      gap: 0.5,
                      opacity: isMobile ? 1 : 0,
                      transition: 'opacity 0.2s',
                      zIndex: 10
                    }}>
                      <IconButton 
                        size="small" 
                        onClick={(e) => { e.stopPropagation(); setEditingItem(item); setConfigDialogOpen(true); }}
                        sx={{ 
                          bgcolor: '#ff9800', 
                          color: 'white',
                          width: 24, height: 24,
                          '&:hover': { bgcolor: '#f57c00' },
                          boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                        }}
                      >
                        <Pencil size={14} />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={(e) => { e.stopPropagation(); handleCopyItem(item); }}
                        sx={{ 
                          bgcolor: '#00d4ff', 
                          color: 'white',
                          width: 24, height: 24,
                          '&:hover': { bgcolor: '#00b8e6' },
                          boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                        }}
                      >
                        <Copy size={14} />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.i); }}
                        sx={{ 
                          bgcolor: 'rgba(255, 0, 0, 0.8)', 
                          color: 'white',
                          width: 24, height: 24,
                          '&:hover': { bgcolor: 'red' },
                          boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                        }}
                      >
                        <Trash2 size={14} />
                      </IconButton>
                    </Box>
                  </Box>
                </div>
              ))}
            </ReactGridLayout>
          </Box>
        </GlassContainer>
      )}

      {/* Config Dialog */}
      <Dialog 
        open={configDialogOpen} 
        onClose={() => setConfigDialogOpen(false)} 
        maxWidth="xs" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            bgcolor: '#1a1a1a',
            backgroundImage: 'none',
            border: isMobile ? 'none' : '1px solid rgba(255,255,255,0.1)',
            borderRadius: isMobile ? 0 : '16px',
            boxShadow: '0 24px 48px rgba(0,0,0,0.5)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#1a1a1a', 
          color: '#fff',
          textAlign: isMobile ? 'center' : 'left',
          pt: isMobile ? 4 : 2
        }}>
          Component Config
        </DialogTitle>
        <DialogContent sx={{ 
          bgcolor: '#1a1a1a', 
          color: '#fff', 
          pt: isMobile ? 4 : 2, 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 3,
          px: isMobile ? 3 : 2
        }}>
          {editingItem && (
            <>
              <TextField
                label="Display Name"
                fullWidth
                size={isMobile ? 'medium' : 'small'}
                value={editingItem.friendly_name || ''}
                onChange={(e) => setEditingItem({ ...editingItem, friendly_name: e.target.value })}
                placeholder={editingItem.entity_id ? 'Uses entity name if empty' : 'Enter custom name'}
                sx={{ '& .MuiOutlinedInput-root': { color: 'white' } }}
              />

              {editingItem.object_type === 'device' && (
              <Autocomplete
                options={haEntities}
                getOptionLabel={(option) => `${option.attributes.friendly_name || option.entity_id} (${option.entity_id})`}
                value={haEntities.find(e => e.entity_id === editingItem.entity_id) || null}
                onChange={(e, val) => setEditingItem({ ...editingItem, entity_id: val?.entity_id || '', friendly_name: val?.attributes?.friendly_name || '' })}
                renderInput={(params) => (
                  <TextField {...params} label="Home Assistant Entity" fullWidth size={isMobile ? 'medium' : 'small'} />
                )}
                sx={{ '& .MuiOutlinedInput-root': { color: 'white' } }}
                slotProps={{
                  paper: {
                    sx: {
                      bgcolor: '#1a1a1a',
                      backgroundImage: 'none',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.1)',
                      '& .MuiAutocomplete-listbox': {
                        '& .MuiAutocomplete-option': {
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                          '&.Mui-focused': { bgcolor: 'rgba(255,255,255,0.15)' }
                        }
                      }
                    }
                  }
                }}
              />
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)' }}>Pick Active Color</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {PRESET_COLORS.map(color => (
                    <Box 
                      key={color}
                      onClick={() => setEditingItem({ ...editingItem, color_hex: color })}
                      sx={{ 
                        width: isMobile ? 36 : 24, 
                        height: isMobile ? 36 : 24, 
                        borderRadius: '50%', 
                        bgcolor: color, 
                        cursor: 'pointer', 
                        border: editingItem.color_hex === color ? '2px solid white' : '1px solid rgba(255,255,255,0.2)',
                        '&:hover': { transform: 'scale(1.1)' }
                      }}
                    />
                  ))}
                  <input 
                    type="color" 
                    value={editingItem.color_hex} 
                    onChange={(e) => setEditingItem({ ...editingItem, color_hex: e.target.value })}
                    style={{ 
                      width: isMobile ? 36 : 24, 
                      height: isMobile ? 36 : 24, 
                      border: 'none', 
                      background: 'none', 
                      cursor: 'pointer' 
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingItem.glow_enabled !== false}
                      onChange={(e) => setEditingItem({ ...editingItem, glow_enabled: e.target.checked })}
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#00d4ff' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#00d4ff' }
                      }}
                    />
                  }
                  label={
                    <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Enable Glow Effect
                    </Typography>
                  }
                />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', ml: 1 }}>
                  Adds a glowing aura and ambient light when the device is active.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 1 }}>
                <Box 
                  sx={{ 
                    width: isMobile ? 64 : 48, 
                    height: isMobile ? 64 : 48, 
                    borderRadius: 2, 
                    bgcolor: 'rgba(255,255,255,0.05)', 
                    border: '1px solid rgba(255,255,255,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer'
                  }} 
                  onClick={() => setIconPickerOpen(true)}
                >
                   {renderIcon(editingItem, isMobile ? 40 : 32, editingItem.color_hex)}
                </Box>
                <Button 
                  variant="outlined" 
                  size={isMobile ? 'medium' : 'small'}
                  startIcon={<Search size={isMobile ? 20 : 16} />}
                  onClick={() => setIconPickerOpen(true)}
                  sx={{ borderRadius: '8px', color: '#00d4ff', borderColor: 'rgba(0,212,255,0.3)' }}
                >
                  Change Icon
                </Button>
              </Box>

              {isPipe(editingItem.object_type) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)' }}>Energy Flow Settings</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ minWidth: 80 }}>Speed: {editingItem.flow_speed?.toFixed(1)}x</Typography>
                    <Slider
                      value={editingItem.flow_speed || 1.0}
                      min={0.1}
                      max={5.0}
                      step={0.1}
                      onChange={(e, val) => setEditingItem({ ...editingItem, flow_speed: val })}
                      sx={{ color: '#00d4ff' }}
                    />
                  </Box>
                  <Autocomplete
                    options={[
                      { id: 'normal', name: 'Normal (Left to Right / Top to Bottom)' },
                      { id: 'reverse', name: 'Reverse (Right to Left / Bottom to Top)' }
                    ]}
                    getOptionLabel={(option) => option.name}
                    value={[
                      { id: 'normal', name: 'Normal (Left to Right / Top to Bottom)' },
                      { id: 'reverse', name: 'Reverse (Right to Left / Bottom to Top)' }
                    ].find(o => o.id === (editingItem.flow_direction || 'normal')) || null}
                    onChange={(e, val) => setEditingItem({ ...editingItem, flow_direction: val?.id || 'normal' })}
                    renderInput={(params) => (
                      <TextField {...params} label="Flow Direction" fullWidth size="small" />
                    )}
                    sx={{ '& .MuiOutlinedInput-root': { color: 'white' } }}
                    slotProps={{
                      paper: {
                        sx: {
                          bgcolor: '#1a1a1a',
                          backgroundImage: 'none',
                          color: 'white',
                          border: '1px solid rgba(255,255,255,0.1)',
                        }
                      }
                    }}
                  />
                  <Autocomplete
                    options={haEntities}
                    getOptionLabel={(option) => `${option.attributes.friendly_name || option.entity_id} (${option.entity_id})`}
                    value={haEntities.find(e => e.entity_id === editingItem.trigger_sensor) || null}
                    onChange={(e, val) => setEditingItem({ ...editingItem, trigger_sensor: val?.entity_id || '' })}
                    renderInput={(params) => (
                      <TextField {...params} label="Trigger Sensor (Optional)" fullWidth size="small" />
                    )}
                    sx={{ '& .MuiOutlinedInput-root': { color: 'white' } }}
                    slotProps={{
                      paper: {
                        sx: {
                          bgcolor: '#1a1a1a',
                          backgroundImage: 'none',
                          color: 'white',
                          border: '1px solid rgba(255,255,255,0.1)',
                          '& .MuiAutocomplete-listbox': {
                            '& .MuiAutocomplete-option': {
                              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                              '&.Mui-focused': { bgcolor: 'rgba(255,255,255,0.15)' }
                            }
                          }
                        }
                      }
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          bgcolor: '#1a1a1a', 
          p: isMobile ? 4 : 2,
          flexDirection: isMobile ? 'column' : 'row',
          gap: 2
        }}>
          <SecondaryButton 
            onClick={() => setConfigDialogOpen(false)}
            sx={{ width: isMobile ? '100%' : 'auto' }}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton 
            onClick={saveItemConfig}
            sx={{ width: isMobile ? '100%' : 'auto' }}
          >
            Save Changes
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <IconPicker 
        open={iconPickerOpen} 
        onClose={() => setIconPickerOpen(false)} 
        fullScreen={isMobile}
        onSelect={(iconData) => {
          if (editingItem) {
            setEditingItem({ ...editingItem, icon_name: iconData.name, icon_type: iconData.type });
          } else {
            handleAddItem(iconData);
            if (isMobile) setDesignerTab(0);
          }
          setIconPickerOpen(false);
        }}
      />
    </Box>
  );
};

export default RoomDesigner;
